import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, Bar, Cell, ReferenceLine,
} from 'recharts';
import { TrendingUp, Calendar } from 'lucide-react';
import { CHART_SERIES } from '../../config/statusColors';
import { PORTFOLIO_PERFORMANCE, BENCHMARK_RETURNS } from '../../data/portfolioMockData';
import ExportToolbar from './ExportToolbar';
import { exportToExcel, exportToPdf, PERFORMANCE_EXPORT_COLUMNS } from '../../lib/exportUtils';

/* -- benchmark line colours ------------------------------------------------ */
const BENCHMARK_COLORS = {
  usd: '#6366F1',
  gbp: '#F59E0B',
  chf: '#10B981',
  aed: '#EF4444',
  jpy: '#8B5CF6',
};

/* -- market returns mock table --------------------------------------------- */
const MARKET_RETURNS = [
  { market: 'Kenya',        '3M': 4.1,  '6M': 8.5,  '1Y': 16.9 },
  { market: 'Nigeria',      '3M': 5.3,  '6M': 10.2, '1Y': 19.4 },
  { market: 'Ghana',        '3M': 3.8,  '6M': 7.1,  '1Y': 14.2 },
  { market: 'Rwanda',       '3M': 2.9,  '6M': 5.8,  '1Y': 11.3 },
  { market: 'Uganda',       '3M': 3.5,  '6M': 6.9,  '1Y': 13.5 },
  { market: 'Tanzania',     '3M': 2.4,  '6M': 4.7,  '1Y': 9.6  },
  { market: 'South Africa', '3M': 2.1,  '6M': 4.2,  '1Y': 8.3  },
  { market: 'Egypt',        '3M': 6.2,  '6M': 11.8, '1Y': 22.1 },
];

/* -- attribution waterfall data -------------------------------------------- */
const ATTRIBUTION_DATA = [
  { name: 'Local Yield', value: 16.2 },
  { name: 'Foreign Exchange Impact',   value: -3.8 },
  { name: 'Fees',        value: -0.5 },
  { name: 'Net Return',  value: 11.9 },
];

const PerformanceTab = ({ positions, performance: perfProp, benchmarks: benchProp }) => {
  const performanceData = perfProp || PORTFOLIO_PERFORMANCE;
  const benchmarkData = benchProp || BENCHMARK_RETURNS;

  const [period, setPeriod] = useState('ITD');
  const [selectedBenchmarks, setSelectedBenchmarks] = useState(['usd']);

  /* -- toggle a benchmark on/off ----------------------------------------- */
  const toggleBenchmark = (b) => {
    setSelectedBenchmarks((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
    );
  };

  /* -- build full chart data with cumulative benchmark returns ------------ */
  const fullChartData = useMemo(() => {
    const benchmarkKeys = ['usd', 'gbp', 'chf', 'aed', 'jpy'];
    const cumBenchmarks = {};
    benchmarkKeys.forEach((k) => { cumBenchmarks[k] = 1; });

    return performanceData.map((perf, i) => {
      const bench = benchmarkData[i] || {};
      const entry = {
        month: perf.month,
        twr: +(perf.cumTwr * 100).toFixed(2),
        mwr: +(perf.cumMwr * 100).toFixed(2),
      };
      benchmarkKeys.forEach((k) => {
        cumBenchmarks[k] *= (1 + (bench[k] || 0));
        entry[k] = +((cumBenchmarks[k] - 1) * 100).toFixed(2);
      });
      return entry;
    });
  }, [performanceData, benchmarkData]);

  /* -- filter chart data by period --------------------------------------- */
  const chartData = useMemo(() => {
    const total = fullChartData.length;
    switch (period) {
      case '3M':  return fullChartData.slice(Math.max(0, total - 3));
      case '6M':  return fullChartData.slice(Math.max(0, total - 6));
      case '1Y':  return fullChartData;
      case 'YTD': return fullChartData.filter((d) => d.month.includes('2026'));
      case 'ITD':
      default:    return fullChartData;
    }
  }, [fullChartData, period]);

  const handleExportExcel = () => {
    exportToExcel(performanceData, PERFORMANCE_EXPORT_COLUMNS, 'Performance', 'portfolio-performance');
  };

  /* -- render ------------------------------------------------------------- */
  return (
    <div className="space-y-6">
      {/* Export Toolbar */}
      <div className="flex justify-end">
        <ExportToolbar onExportExcel={handleExportExcel} onExportPdf={exportToPdf} />
      </div>

      {/* -- Period selector + benchmark toggles -------------------------- */}
      <div className="bg-white rounded-none border border-zinc-200 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono uppercase tracking-wider">
          <Calendar className="w-3.5 h-3.5" />
          Period
        </div>
        <div className="flex gap-2">
          {['3M', '6M', '1Y', 'YTD', 'ITD'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border ${
                period === p
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-zinc-600 border-zinc-300 hover:border-[#4D4D4D]'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        <div className="border-l border-zinc-300 pl-4 flex items-center gap-2">
          <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Benchmarks</span>
          {['usd', 'gbp', 'chf', 'aed', 'jpy'].map((b) => (
            <button
              key={b}
              onClick={() => toggleBenchmark(b)}
              className={`px-2 py-1 text-xs font-mono uppercase border ${
                selectedBenchmarks.includes(b)
                  ? 'bg-zinc-100 border-zinc-400'
                  : 'bg-white border-zinc-200 text-zinc-400'
              }`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* -- TWR / MWR Line Chart ----------------------------------------- */}
      <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-zinc-500" />
          <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-700">
            Portfolio Returns — Time-Weighted vs Money-Weighted
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fontFamily: 'monospace' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fontFamily: 'monospace' }}
              tickFormatter={(v) => `${v}%`}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name.toUpperCase()]}
              contentStyle={{ fontFamily: 'monospace', fontSize: 12, border: '1px solid #e4e4e7' }}
            />
            <Legend
              wrapperStyle={{ fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase' }}
            />
            <Line
              type="monotone"
              dataKey="twr"
              name="TWR"
              stroke="#2B5EA7"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="mwr"
              name="MWR"
              stroke="#1A7A8A"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 4 }}
            />
            {selectedBenchmarks.map((b) => (
              <Line
                key={b}
                type="monotone"
                dataKey={b}
                name={b.toUpperCase()}
                stroke={BENCHMARK_COLORS[b]}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* -- Two-column: Attribution + Market Returns --------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* -- Return Attribution Waterfall ------------------------------- */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-700 mb-4">
            Return Attribution (ITD)
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={ATTRIBUTION_DATA}
              margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fontFamily: 'monospace' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fontFamily: 'monospace' }}
                tickFormatter={(v) => `${v}%`}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Return']}
                contentStyle={{ fontFamily: 'monospace', fontSize: 12, border: '1px solid #e4e4e7' }}
              />
              <ReferenceLine y={0} stroke="#a1a1aa" strokeWidth={1} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {ATTRIBUTION_DATA.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={entry.value >= 0 ? CHART_SERIES[0] : '#FFC1DA'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* -- Returns by Market Table ------------------------------------ */}
        <div className="bg-white rounded-none shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-zinc-700 mb-4">
            Returns by Market
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-2 pr-4 uppercase tracking-wider text-zinc-500 font-medium">
                    Market
                  </th>
                  <th className="text-right py-2 px-4 uppercase tracking-wider text-zinc-500 font-medium">
                    3M
                  </th>
                  <th className="text-right py-2 px-4 uppercase tracking-wider text-zinc-500 font-medium">
                    6M
                  </th>
                  <th className="text-right py-2 pl-4 uppercase tracking-wider text-zinc-500 font-medium">
                    1Y
                  </th>
                </tr>
              </thead>
              <tbody>
                {MARKET_RETURNS.map((row) => (
                  <tr key={row.market} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-2 pr-4 text-zinc-700 font-medium">{row.market}</td>
                    {['3M', '6M', '1Y'].map((col) => {
                      const val = row[col];
                      return (
                        <td
                          key={col}
                          className="text-right py-2 px-4"
                          style={{ color: val >= 0 ? '#16a34a' : '#dc2626' }}
                        >
                          {val >= 0 ? '+' : ''}{val.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab;
